import { Component, FunctionComponent } from "react";
import { Form, Alert, Col, Container, Row } from "react-bootstrap";
import { Auth } from "aws-amplify";
import { RouteComponentProps } from "react-router-dom";
import { Color, Path, Storage } from "../../../env";
import { Button, icon, Icon, variant } from "../../form/Button";
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';

export enum Action {
    Register
}

export interface CredentialsState {
    action: Action
    termsAgreed: boolean;
    fields: Map<formAttribute, string>;
    error: string;
    inviteCode: string;
    redirect?: string;
}

export interface CredentialsProps extends RouteComponentProps {
    auth: typeof Auth;
}

export enum formAttribute {
    username = "username",
    email = "email",
    password = "password",
}

export default class Credentials extends Component<
    CredentialsProps,
    CredentialsState
> {
    constructor(props: CredentialsProps) {
        super(props);
        const queryParams = new URLSearchParams(this.props.history.location.search)
        const email = queryParams.get("email") || ""
        const redirect = queryParams.get("redirect") || undefined
        this.state = {
            action: Action.Register,
            termsAgreed: false,
            fields: new Map([
                [formAttribute.username, ""],
                [formAttribute.email, email],
                [formAttribute.password, ""],
            ]),
            error: "",
            inviteCode: "",
            redirect: redirect
        };
    }

    componentDidMount(): void {
        this.isLoggedIn().then(isLoggedIn => {
            if (isLoggedIn) {
                localStorage.setItem(Storage.IsLoggedIn, "true");

                if (this.state.redirect) {
                    return this.props.history.push(this.state.redirect)
                }

                this.props.history.push(Path.ShopDashboard);
            }
        })
    }

    isLoggedIn = async (): Promise<boolean> => {
        try {
            await this.props.auth.currentAuthenticatedUser();
            return true;
        } catch {
            return false;
        }
    }

    handleChange = (name: formAttribute, value: string) => {
        const fields = this.state.fields;
        fields.set(name, value);
        this.setState({
            fields: fields,
        });
    };

    isValidField = (
        field: formAttribute,
        fields: Map<formAttribute, string>
    ): boolean => {
        return (
            typeof fields.get(field) !== "undefined" &&
            fields.get(field)!.trim() !== ""
        );
    };

    handleNavigate = (path: Path) => {
        this.props.history.push(path);
    };

    isValidUsername = (value: string): boolean => {
        const regexPattern = /^[\p{Letter}\p{Mark}\p{Symbol}\p{Number}\p{Punctuation}]+$/u;
        return regexPattern.test(value);
    }

    handleSubmit = () => {
        if (
            this.isValidField(formAttribute.username, this.state.fields) ===
            false
        ) {
            const error = "Please provide your username";
            return this.setState({
                error: error,
            });
        }

        const username: string = this.state.fields
            .get(formAttribute.username)!
            .trim();

        if (!this.isValidUsername(username)) {
            return this.setState({
                error: "Username is invalid. Username cannot contain spaces and must be alphanumeric."
            })
        }

        if (
            this.isValidField(formAttribute.password, this.state.fields) ===
            false
        ) {
            const error = "Please provide a password";
            return this.setState({
                error: error,
            });
        }

        if (
            this.isValidField(formAttribute.email, this.state.fields) === false
        ) {
            const error = "Please provide your email";
            return this.setState({
                error: error,
            });
        }

        const password: string = this.state.fields
            .get(formAttribute.password)!
            .trim();
        const email: string = this.state.fields
            .get(formAttribute.email)!
            .trim();

        this.props.auth
            .signUp({
                username,
                password,
                attributes: {
                    email: email,
                },
            })
            .then(() => {
                this.props.history.push("/register/member/confirmation");
            })
            .catch((err) =>
                this.setState({
                    error: err.message,
                })
            );
    };

    handleAgreementChange = () => {
        this.setState({
            termsAgreed: !this.state.termsAgreed,
        })
    }

    handleInviteCodeChange = (value: string) => {
        this.setState({
            inviteCode: value,
        })
    }

    handleFederatedLogin = async (type: CognitoHostedUIIdentityProvider) => {
        this.props.auth.federatedSignIn({
            provider: type,
        })
    }

    render() {
        const credentialFormProps: CredentialsFormProps = {
            termsAgreed: this.state.termsAgreed,
            username: this.state.fields.get(formAttribute.username)!,
            email: this.state.fields.get(formAttribute.email)!,
            password: this.state.fields.get(formAttribute.password)!,
            error: this.state.error,
            onAgreementChange: this.handleAgreementChange,
            onChange: this.handleChange,
            onSubmit: this.handleSubmit,
            onNavigate: this.handleNavigate,
            onFederatedLogin: this.handleFederatedLogin
        };
        return <CredentialsForm {...credentialFormProps} />;
    }
}

export interface CredentialsFormProps {
    termsAgreed: boolean;
    username: string;
    email: string;
    password: string;
    error: string;
    onAgreementChange: () => void;
    onChange: (name: formAttribute, value: string) => void;
    onSubmit: () => void;
    onNavigate: (path: Path) => void;
    onFederatedLogin: (type: CognitoHostedUIIdentityProvider) => void;
}

export const CredentialsForm: FunctionComponent<CredentialsFormProps> = (
    props
) => (
    <>
        <h1 style={{ marginBottom: "2rem" }}>Enter credentials</h1>
        <Form>
            <Alert variant={"danger"} show={props.error != ""}>
                {props.error}
            </Alert>
            <Form.Group style={{ marginTop: ".7rem" }} controlId="formBasicUsername">
                <Form.Label>Username</Form.Label>
                <Form.Control
                    id={"username"}
                    type="text"
                    name={"username"}
                    onChange={(e) =>
                        props.onChange(formAttribute.username, e.target.value)
                    }
                />
            </Form.Group>
            <Form.Group style={{ marginTop: ".7rem" }} controlId="formBasicEmail">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                    id={"email"}
                    type="email"
                    name={"email"}
                    value={props.email}
                    onChange={(e) =>
                        props.onChange(formAttribute.email, e.target.value)
                    }
                />
            </Form.Group>
            <Form.Group style={{ marginTop: ".7rem" }} controlId="formBasicPassword">
                <Form.Label>Password</Form.Label>
                <Form.Control
                    id={"password"}
                    type="password"
                    name={"password"}
                    onChange={(e) =>
                        props.onChange(formAttribute.password, e.target.value)
                    }
                />
            </Form.Group>
            <Form.Group style={{ padding: "2rem 0 1rem 0" }} controlId="termsConfirmation">
                <Form.Check
                    checked={props.termsAgreed}
                    onClick={() => props.onAgreementChange()}
                    label={
                        <Form.Label style={{ fontSize: ".9rem" }}>I agree to the <a href="https://stumbled.online/terms.html" target="_">Terms and Conditions</a> and the <a href="https://stumbled.online/privacy.html" target="_">Privacy Policy</a></Form.Label>
                    }
                />
            </Form.Group>
            <Button id={"register-btn"} style={{ width: "100%" }} onClick={() => props.onSubmit()} buttonvariant={variant.Primary} name={"Submit"} disabled={!props.termsAgreed} />
            <div style={{ textAlign: "center" }}>
                <span style={{ textAlign: "center", fontSize: ".9rem" }}>Have an account? <a
                    style={{
                        color: Color.Primary,
                        cursor: "pointer",
                        marginTop: ".5rem"
                    }}
                    onClick={() =>
                        props.onNavigate(
                            Path.Login
                        )
                    }
                >
                    Login
                </a></span>
            </div>
        </Form>
        <div style={{ width: "100%", height: "20px", borderBottom: `1px solid ${Color.Grey}`, textAlign: "center", marginTop: "2rem", marginBottom: "1rem" }}>
            <span style={{ padding: "0 10px", backgroundColor: Color.White, fontSize: "1.2rem", color: Color.DarkGrey }}>
                Or
            </span>
        </div>
        <Row>
            <Col xs={12}>
                <button style={{ border: 0, background: "#436BC6", padding: ".4rem", color: Color.White, width: "100%", marginTop: "1rem" }} onClick={() => props.onFederatedLogin(CognitoHostedUIIdentityProvider.Facebook)}><Icon icon={icon.Facebook} size={30} /> Continue with Facebook</button>
                <button style={{ border: 0, background: "#DE4A3A", padding: ".4rem", color: Color.White, width: "100%", marginTop: "1rem" }} onClick={() => props.onFederatedLogin(CognitoHostedUIIdentityProvider.Google)}><Icon icon={icon.Google} size={30} /> Continue with Google</button>
            </Col>
        </Row>
    </>
);
